import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Signal,
  ViewChild,
  computed,
  signal,
  SimpleChanges,
} from '@angular/core';
import { CdkDragEnd, CdkDragStart } from '@angular/cdk/drag-drop';

export interface CalendarTask {
  id: string;
  title: string;
  start: Date; // inclusive
  end: Date;   // exclusive
  color?: string; // optional accent color
  data?: any;     // custom payload
}

export interface WeekViewChange {
  taskId: string;
  newStart: Date;
  newEnd: Date;
}

// Helper to get start of week (Mon) per Europe/Copenhagen locale-like behavior
function startOfWeek(d: Date): Date {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0..Sun=6
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

//MOCK DATA

  function on(dayOffset: number, h: number, m = 0) {
  const d = startOfWeek(new Date());
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return d;
}

export let MOCK_TASKS: CalendarTask[] = [
  { id: '1', title: 'Standup',        start: on(0, 9, 0),  end: on(0, 9, 30),  color: '#3b82f6' }, // Mon
  { id: '2', title: 'Design Review',  start: on(1, 11, 0), end: on(1, 12, 0),  color: '#10b981' }, // Tue
  { id: '3', title: 'Client Call',    start: on(2, 14, 0), end: on(2, 15, 0),  color: '#f59e0b' }, // Wed
  { id: '4', title: 'Deep Work',      start: on(3, 13, 0), end: on(3, 16, 0),  color: '#8b5cf6' }, // Thu
  { id: '5', title: 'Deploy',         start: on(4, 15, 0), end: on(4, 16, 30), color: '#ef4444' }, // Fri
  // Overlapping example (same day/time window)
  { id: '6', title: '1:1',            start: on(2, 14, 30), end: on(2, 15, 0), color: '#06b6d4' }, // Wed overlap
];


@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnChanges {
  // Inputs
  @Input({ required: true }) tasks: CalendarTask[] = [];
  @Input() weekAnchor: Date = new Date(); // any date within the desired week
  @Input() slotMinutes = 30; // 30-minute slots
  @Input() slotPixelHeight = 24; // px per 30 mins (adjust for density)
  @Input() startHour = 0; // first hour visible
  @Input() endHour = 24;  // last hour visible (exclusive)

  // Outputs
  @Output() taskMoved = new EventEmitter<WeekViewChange>();

  // View refs
  @ViewChild('grid', { static: true }) gridRef!: ElementRef<HTMLElement>;

  // Internal signals
  weekStart = signal<Date>(startOfWeek(new Date())); // default to current week
  days: Signal<Date[]> = computed(() => {
    const base = this.weekStart();
    return Array.from({ length: 7 }, (_, i) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + i));
  });

  hours: Signal<number[]> = computed(() =>
    Array.from({ length: this.endHour - this.startHour }, (_, i) => i + this.startHour)
  );
  slotsPerHour: Signal<number> = computed(() => Math.floor(60 / this.slotMinutes));
  totalSlots: Signal<number> = computed(() => (this.endHour - this.startHour) * this.slotsPerHour());

  // Layout (computed on resize)
  gridWidth = signal(0);
  columnWidth = computed(() => (this.gridWidth() > 0 ? this.gridWidth() / 7 : 0));

  // Drag state
  private dragStartOffset: { x: number; y: number } | null = null;
  private draggingTaskId: string | null = null;

  // React to parent-provided weekAnchor if it changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weekAnchor'] && this.weekAnchor instanceof Date && !isNaN(this.weekAnchor.getTime())) {
      this.weekStart.set(startOfWeek(this.weekAnchor));
    }
  }

  ngOnInit() {
    // Initialize with current week if no valid weekAnchor provided
    if (!(this.weekAnchor instanceof Date) || isNaN(this.weekAnchor.getTime())) {
      this.weekAnchor = new Date();
    }
    this.weekStart.set(startOfWeek(this.weekAnchor));

    // Initialize grid width once view is available
    queueMicrotask(() => this.measure());
    this.tasks = MOCK_TASKS;
  }

  // Recalculate on resize
  @HostListener('window:resize') onResize() {
    this.measure();
  }
  private measure() {
    if (!this.gridRef?.nativeElement) return;
    const rect = this.gridRef.nativeElement.getBoundingClientRect();
    this.gridWidth.set(rect.width);
  }

  // Public API: navigate weeks (no routing)
  prevWeek() {
    const d = new Date(this.weekStart());
    d.setDate(d.getDate() - 7);
    this.weekStart.set(d);
  }
  nextWeek() {
    const d = new Date(this.weekStart());
    d.setDate(d.getDate() + 7);
    this.weekStart.set(d);
  }
  today() {
    this.weekStart.set(startOfWeek(new Date()));
  }

  // Convenience wrappers for toolbar buttons (if you want different names there)
  gotoPrevWeek() { this.prevWeek(); }
  gotoNextWeek() { this.nextWeek(); }
  gotoToday() { this.today(); }

  // Derived layout for each task (absolute positioning)
  // Returns style object with left/top/height/width
  taskStyle(task: CalendarTask): any {
    const start = task.start;
    const end = task.end;
    const dayIndex = this.dayIndexForDate(start);
    const minutesFromStartOfDay = (start.getHours() * 60 + start.getMinutes()) - this.startHour * 60;
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;

    const topPx = (minutesFromStartOfDay / this.slotMinutes) * this.slotPixelHeight;
    const heightPx = (durationMinutes / this.slotMinutes) * this.slotPixelHeight - 2; // -2 for gap

    return {
      left: `${dayIndex * this.columnWidth()}px`,
      top: `${topPx}px`,
      height: `${Math.max(heightPx, this.slotPixelHeight - 2)}px`,
      width: `${this.columnWidth()}px`,
      borderLeft: task.color ? `4px solid ${task.color}` : undefined,
    };
  }

  // Handle drag start/end using free-drag within the whole grid
  onDragStart(ev: CdkDragStart, task: CalendarTask) {
    const pos = (ev.source.getFreeDragPosition?.() as { x: number; y: number }) || { x: 0, y: 0 };
    this.dragStartOffset = { x: pos.x, y: pos.y };
    this.draggingTaskId = task.id;
  }

  onDragEnd(ev: CdkDragEnd, task: CalendarTask) {
    const gridEl = this.gridRef.nativeElement;
    const gridRect = gridEl.getBoundingClientRect();
    const handle = ev.source.element.nativeElement as HTMLElement;
    const handleRect = handle.getBoundingClientRect();

    // Compute center point of the dragged element to choose the day
    const centerX = handleRect.left + handleRect.width / 2;
    const centerY = handleRect.top + 8; // near the top edge of the card

    const xInGrid = clamp(centerX - gridRect.left, 0, gridRect.width - 1);
    const yInGrid = clamp(centerY - gridRect.top, 0, gridRect.height - 1);

    const colW = this.columnWidth();
    const dayIdxRaw = colW > 0 ? Math.floor(xInGrid / colW) : 0;
    const dayIdx = clamp(dayIdxRaw, 0, 6);

    // snap Y to nearest slot
    const slotIndexFloat = yInGrid / this.slotPixelHeight;
    const slotIndex = clamp(Math.round(slotIndexFloat), 0, this.totalSlots() - 1);

    const minutesFromStart = slotIndex * this.slotMinutes;
    const hours = Math.floor(minutesFromStart / 60) + this.startHour;
    const minutes = minutesFromStart % 60;

    const targetDate = new Date(this.days()[dayIdx]);
    targetDate.setHours(hours, minutes, 0, 0);

    const durationMs = task.end.getTime() - task.start.getTime();
    const targetEnd = new Date(targetDate.getTime() + durationMs);

    // Emit and reset drag state
    this.taskMoved.emit({ taskId: task.id, newStart: targetDate, newEnd: targetEnd });
    this.dragStartOffset = null;
    this.draggingTaskId = null;

    // Reset the element position so it re-renders at computed style from task data
    ev.source.reset();
  }

  // Utilities
  private dayIndexForDate(d: Date): number {
    const ws = this.weekStart();
    const start = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate());
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.floor((date.getTime() - start.getTime()) / 86400000);
    return clamp(diffDays, 0, 6);
  }

  trackByTask = (_: number, t: CalendarTask) => t.id;

  
}


